import React, { useState, useCallback, useEffect, ReactNode } from 'react';
import { authServiceInstance as authService } from '../../../services/authServiceInstance';
import { AuthState, AuthContextType, AuthStateType } from '../authTypes';
import { AuthContext } from './authContext';

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    authStateType: AuthStateType.INITIALIZING,
    error: null,
  });

  const initializeAuth = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        authStateType: AuthStateType.LOADING,
        error: null,
      }));

      // Load auth session
      const session = await authService.loadSession();

      if (session?.user) {
        setState({
          isAuthenticated: true,
          user: session.user,
          authStateType: AuthStateType.AUTHENTICATED,
          error: null,
        });
      } else {
        setState({
          isAuthenticated: false,
          user: null,
          authStateType: AuthStateType.UNAUTHENTICATED,
          error: null,
        });
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        authStateType: AuthStateType.UNAUTHENTICATED,
        error: error instanceof Error ? error.message : 'Authentication failed',
      }));
    }
  }, []);

  useEffect(() => {
    initializeAuth();

    // Set up listeners for auth events
    const handleAuthEvent = () => {
      initializeAuth();
    };

    // Auth events
    authService.addEventListener('login', handleAuthEvent);
    authService.addEventListener('logout', handleAuthEvent);
    authService.addEventListener('session-updated', handleAuthEvent);
    authService.addEventListener('token-refreshed', handleAuthEvent);

    return () => {
      // Clean up auth event listeners
      authService.removeEventListener('login', handleAuthEvent);
      authService.removeEventListener('logout', handleAuthEvent);
      authService.removeEventListener('session-updated', handleAuthEvent);
      authService.removeEventListener('token-refreshed', handleAuthEvent);
    };
  }, [initializeAuth]);

  const logout = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        authStateType: AuthStateType.LOADING,
        error: null,
      }));
      await authService.logout();
      setState({
        isAuthenticated: false,
        user: null,
        authStateType: AuthStateType.UNAUTHENTICATED,
        error: null,
      });
    } catch (error) {
      setState(prev => ({
        ...prev,
        authStateType: AuthStateType.UNAUTHENTICATED,
        error: error instanceof Error ? error.message : 'Logout failed',
      }));
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    await initializeAuth();
  }, [initializeAuth]);

  // Compute isLoading based on authStateType
  const isLoading =
    state.authStateType === AuthStateType.INITIALIZING ||
    state.authStateType === AuthStateType.LOADING;

  // Create the context value object
  const contextValue: AuthContextType = {
    ...state,
    logout,
    refreshAuth,
    isLoading,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
